<template>
  <CustomizationFunnel
    :is-services-pickers-response-pending
    :pickers="pickersResponse"
    :pickers-services="pickersServices?.pickerGroups ?? []"
    :price
    :price-without-subsidies
    :price-without-trade-in
    :product
    :product-tracking
    :selected-mobile-plan
    :selected-offer
    :upgrade-picker-fields="upgradeFields"
  >
    <template #sticky-bar="{ currentStep, totalSteps, selectedOptions }">
      <RevPortal defer :to="`#${STICKY_BARS_PORTAL_TARGET}`">
        <StickyBarTop
          :price
          :price-without-subsidies
          :product
          :selected-mobile-plan
          :selected-offer
          :selected-options
          :tracking="productTracking"
          :upgrade-picker-fields="upgradeFields"
        >
          <template #progress-bar>
            <ProgressBar :current-step :total-steps />
          </template>
        </StickyBarTop>

        <StickyBarTopSmall
          :select="selectedOffer.shipping"
          :selected-options
          :shipping="selectedOffer.shipping"
          :upgrade-picker-fields="upgradeFields"
        >
          <template #progress-bar>
            <ProgressBar :current-step :total-steps />
          </template>
        </StickyBarTopSmall>
        <ClientOnly>
          <UpgradeForLess
            :pickers="pickersResponse"
            :product
            @upgrade-accepted="handleUpgradeAccepted"
          />
        </ClientOnly>
      </RevPortal>
    </template>
    <template #details>
      <div>
        <Accessories
          :comes-with="product.comesWith"
          :partner-promo-codes="product.includedServiceOffers.partnerPromoCodes"
        />

        <!-- TODO after we roll out M&M on all cats -->
        <!-- Should be above reassurance items in the proposal or after reassurance when no pickers -->
        <SellerComment
          v-if="selectedOffer && selectedOffer.sellerComment"
          :comment="selectedOffer.sellerComment"
        />
      </div>
    </template>
  </CustomizationFunnel>
</template>

<script lang="ts" setup>
import { ref, watch } from 'vue'

import type { Price } from '@backmarket/http-api'
import type {
  GetPickersResponse,
  GetServicesPickersResponse,
} from '@backmarket/http-api/src/api-specs-navigation-experience/product/pickers'
import type { GetProductResponse } from '@backmarket/http-api/src/api-specs-navigation-experience/product/product'
import type { MobilePlanOffer } from '@backmarket/http-api/src/api-specs-partnerships/mobile-plan/types/mobile-plan-offers'
import { RevPortal } from '@ds/components/Portal'

import { ClientOnly } from '#components'

import { STICKY_BARS_PORTAL_TARGET } from '~/scopes/product/constants'
import StickyBarTop from '~/scopes/product-core/components/presentationals//CustomizationFunnel/components/StickyBarTop/StickyBarTop.vue'
import StickyBarTopSmall from '~/scopes/product-core/components/presentationals//CustomizationFunnel/components/StickyBarTopSmall/StickyBarTopSmall.vue'
import UpgradeForLess from '~/scopes/product-core/components/presentationals//CustomizationFunnel/components/UpgradeForLess/UpgradeForLess.vue'
import Accessories from '~/scopes/product-core/components/presentationals/Accessories/Accessories.vue'
import CustomizationFunnel from '~/scopes/product-core/components/presentationals/CustomizationFunnel/CustomizationFunnel.vue'
import ProgressBar from '~/scopes/product-core/components/presentationals/CustomizationFunnel/components/ProgressBar/ProgressBar.vue'
import SellerComment from '~/scopes/product-core/components/presentationals/SellerComment/SellerComment.vue'
import { useProductTracking } from '~/scopes/product-core/composables/useFetch/useFetchProductTracking/composables/useProductTracking/useProductTracking'

const props = defineProps<{
  selectedOffer: NonNullable<GetPickersResponse['selectedOffer']>
  pickersResponse: GetPickersResponse
  pickersServices: GetServicesPickersResponse | undefined
  isServicesPickersResponsePending: boolean
  price: Price
  priceWithoutSubsidies?: Price | null
  priceWithoutTradeIn?: Price | null
  product: GetProductResponse
  selectedMobilePlan?: MobilePlanOffer
  productTracking: ReturnType<typeof useProductTracking>
}>()

const upgradeFields = ref<
  NonNullable<GetPickersResponse['upgrade']>['pickerFields'] | undefined
>(undefined)

function handleUpgradeAccepted(
  pickerFields?: NonNullable<GetPickersResponse['upgrade']>['pickerFields'],
) {
  upgradeFields.value = pickerFields
}

watch(
  () => props.pickersResponse,
  (_newValue, oldValue) => {
    // !oldValue.upgrade means that the previous selection was the upgraded one
    if (!oldValue.upgrade && upgradeFields.value) {
      upgradeFields.value = undefined
    }
  },
)
</script>
